import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { cartAPI } from '@/services/api';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [cart, setCart] = useState({
    items: [],
    subtotal: 0,
    discount: 0,
    total: 0,
    promo_code: null,
    promo_details: null,
    item_count: 0,
  });
  const [loading, setLoading] = useState(true); // Start as true to prevent premature redirects
  const [cartCount, setCartCount] = useState(0);

  // Fetch cart when user is authenticated
  const fetchCart = useCallback(async () => {
    if (!isAuthenticated) {
      // Don't set loading to false here - wait for auth to settle
      // This prevents premature redirects
      setCart({
        items: [],
        subtotal: 0,
        discount: 0,
        total: 0,
        promo_code: null,
        promo_details: null,
        item_count: 0,
      });
      setCartCount(0);
      // Keep loading true so checkout waits for auth
      return;
    }

    try {
      setLoading(true);
      const response = await cartAPI.get();
      setCart(response.data);
      setCartCount(response.data.item_count || 0);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    fetchCart();
  }, [fetchCart, user]);

  // Add item to cart
  const addToCart = async (itemType, itemId, variant = 'live') => {
    if (!isAuthenticated) {
      toast.error('Please sign in to add items to cart');
      return { success: false, requiresAuth: true };
    }

    try {
      setLoading(true);
      const response = await cartAPI.add({
        item_type: itemType,
        item_id: itemId,
        variant: variant,
      });
      
      setCartCount(response.data.cart_count);
      toast.success(`${response.data.item?.title || 'Item'} added to cart`);
      
      // Refresh full cart
      await fetchCart();
      
      return { success: true, data: response.data };
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to cart');
      return { success: false, error: error.response?.data?.detail };
    } finally {
      setLoading(false);
    }
  };

  // Remove item from cart
  const removeFromCart = async (itemId, variant = 'live') => {
    try {
      setLoading(true);
      const response = await cartAPI.remove(itemId, variant);
      setCartCount(response.data.cart_count);
      
      // Refresh full cart
      await fetchCart();
      
      toast.success('Item removed from cart');
      return { success: true };
    } catch (error) {
      toast.error('Failed to remove item');
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  // Clear cart
  const clearCart = async () => {
    try {
      setLoading(true);
      await cartAPI.clear();
      setCart({
        items: [],
        subtotal: 0,
        discount: 0,
        total: 0,
        promo_code: null,
        promo_details: null,
        item_count: 0,
      });
      setCartCount(0);
      toast.success('Cart cleared');
      return { success: true };
    } catch (error) {
      toast.error('Failed to clear cart');
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  // Apply promo code
  const applyPromoCode = async (code) => {
    try {
      setLoading(true);
      const response = await cartAPI.applyPromo(code);
      
      toast.success(`Promo code "${response.data.promo_code}" applied! You save $${response.data.discount_amount}`);
      
      // Refresh cart to get updated totals
      await fetchCart();
      
      return { success: true, data: response.data };
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid promo code');
      return { success: false, error: error.response?.data?.detail };
    } finally {
      setLoading(false);
    }
  };

  // Remove promo code
  const removePromoCode = async () => {
    try {
      setLoading(true);
      await cartAPI.removePromo();
      
      toast.success('Promo code removed');
      
      // Refresh cart
      await fetchCart();
      
      return { success: true };
    } catch (error) {
      toast.error('Failed to remove promo code');
      return { success: false };
    } finally {
      setLoading(false);
    }
  };

  // Update item quantity
  const updateItemQuantity = async (itemId, variant, quantity) => {
    try {
      // Update locally first for immediate UI feedback
      setCart(prev => ({
        ...prev,
        items: prev.items.map(item => 
          item.item_id === itemId && item.variant === variant
            ? { ...item, quantity }
            : item
        ),
      }));
      
      // Update on server
      const response = await cartAPI.updateQuantity(itemId, variant, quantity);
      
      // Refresh cart to get accurate totals
      await fetchCart();
      
      return { success: true };
    } catch (error) {
      // Revert on error
      await fetchCart();
      toast.error('Failed to update quantity');
      return { success: false };
    }
  };

  // Checkout
  const checkout = async (checkoutData) => {
    try {
      setLoading(true);
      const response = await cartAPI.checkout(checkoutData);
      
      if (response.data.success) {
        if (response.data.payment_required && response.data.client_secret) {
          // Embedded Checkout: the page opens the Stripe payment form in a modal
          // (stays on our domain). Return the session data to the caller.
          return { success: true, data: response.data };
        } else if (!response.data.payment_required) {
          // Order completed (free or 100% discount)
          toast.success('Order completed successfully!');
          await fetchCart(); // Clear cart
        }
        return { success: true, data: response.data };
      }
      
      return { success: false, error: 'Checkout failed' };
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Checkout failed');
      return { success: false, error: error.response?.data?.detail };
    } finally {
      setLoading(false);
    }
  };

  const value = {
    cart,
    cartCount,
    loading,
    addToCart,
    removeFromCart,
    updateItemQuantity,
    clearCart,
    applyPromoCode,
    removePromoCode,
    checkout,
    refreshCart: fetchCart,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
